import {
  Alert,
  Button,
  Card,
  Heading,
  Link,
  Skeleton,
  Tag,
  Tooltip,
} from "@digdir/designsystemet-react";
import { CheckmarkIcon, FileCsvFillIcon } from "@navikt/aksel-icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type React from "react";
import { useTranslation } from "react-i18next";
import { httpClient } from "@/api/client";
import type { AvtalePeriodeSummaryWithEksportMetadataDto } from "@/api/generated";
import {
  getAvtalerSummaryForLeverandørOptions,
  getAvtalerSummaryForLeverandørQueryKey,
} from "@/api/generated/@tanstack/react-query.gen";
import { HjelpeTekst } from "@/components/HjelpeTekst";
import { cn } from "@/utils";
import {
  formatDateString,
  formatDateStringInterval,
} from "@/utils/dateTimeUtils";
import { useSelskaper } from "../selskap/SelskapContext";

export const BestillingListe: React.FC = () => {
  const { t } = useTranslation();
  const { active } = useSelskaper();

  const activeOrganizationNumber = active?.organisasjonsnummer;
  const { data, isLoading, isError } = useQuery({
    enabled: activeOrganizationNumber != null,
    ...getAvtalerSummaryForLeverandørOptions({
      path: {
        // biome-ignore lint/style/noNonNullAssertion: Query disabled if null
        organisasjonsNummer: activeOrganizationNumber!,
      },
      client: httpClient,
    }),
  });

  if (activeOrganizationNumber == null) {
    return (
      <Alert data-color={"warning"}>
        {t("pages.bestillinger.ingen-selskap")}
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-9">
        <Skeleton width={600} variant="rectangle" className="max-w-card h-70" />
        <Skeleton width={600} variant="rectangle" className="max-w-card h-70" />
        <Skeleton width={600} variant="rectangle" className="max-w-card h-70" />
      </div>
    );
  }

  if (isError) {
    return (
      <Alert data-color={"danger"}>{t("pages.bestillinger.laste-feil")}</Alert>
    );
  }

  if (data == null || data.periodeSummaries.length === 0) {
    return <Alert>{t("pages.bestillinger.ingen-bestillinger")}</Alert>;
  }

  return (
    <div className="space-y-6">
      {data.periodeSummaries.map((periode) => (
        <Card
          key={periode.summary.endDate}
          className="relative md:grid md:grid-cols-2 md:grid-rows-2 max-w-card"
        >
          <AvtalePeriode
            periode={periode}
            className="md:col-start-1 md:row-start-1 md:col-span-1 md:row-span-1"
          />
          <NyeEndringerSidenSistCsvEksport
            periode={periode}
            className="md:col-start-1 md:row-start-2 md:col-span-1 md:row-span-1"
          />
          <EksporterButton
            activeOrganizationNumber={activeOrganizationNumber}
            periode={periode}
            className="md:col-start-2 md:row-start-1 md:col-span-1 md:row-span-1 md:place-self-end md:text-right"
          />
          <SistEksportertOgOppdatert
            periode={periode}
            className="md:col-start-2 md:row-start-2 md:col-span-1 md:row-span-1 md:place-self-end md:text-right"
          />
        </Card>
      ))}
    </div>
  );
};

const AvtalePeriode = ({
  periode,
  className,
}: {
  periode: AvtalePeriodeSummaryWithEksportMetadataDto;
  className?: string;
}) => {
  const { t } = useTranslation();

  const getAvtalePeriodeYear = (): number | null => {
    const date = new Date(periode.summary.endDate);
    if (date.toString() === "Invalid Date") {
      console.error(
        "Could not parse summary end date: ",
        periode.summary.endDate,
      );
      return null;
    }

    if (date.getMonth() !== 0 || date.getDate() !== 1) {
      console.error(
        `Unexpected summary end date for periode ${periode.summary.endDate} for leverandør ${periode.summary.leverandørNummer}, expected end date to be January 1st`,
      );
      return null;
    }

    return date.getFullYear() - 1; // Expecting end date to be exclusive (January 1st)
  };

  return (
    <div className={cn("space-y-2", className)}>
      {periode.summary.summaryData.aktiveAvtaler > 0 && (
        <Tag data-color={"success"}>
          {t("pages.bestillinger.aktive-avtaler-tag")}:{" "}
          {periode.summary.summaryData.aktiveAvtaler}
        </Tag>
      )}
      <div>
        <Heading level={3}>
          {"Avtaleperiode "}
          {getAvtalePeriodeYear()}
        </Heading>
        <span className="text-balder-text-subtle">
          {formatDateStringInterval(
            periode.summary.startDate,
            periode.summary.endDate,
          )}
        </span>
      </div>
    </div>
  );
};

const EksporterButton = ({
  periode,
  activeOrganizationNumber,
  className,
}: {
  periode: AvtalePeriodeSummaryWithEksportMetadataDto;
  activeOrganizationNumber: number | undefined;
  className?: string;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const baseUrl = httpClient.getConfig().baseURL;
  const endDateParam = encodeURIComponent(periode.summary.endDate);
  const url = `${baseUrl}/selskapsportal/avtaler/${activeOrganizationNumber}/csv?periodeEndDate=${endDateParam}`;

  const handleClick = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    event.preventDefault();
    window.location.href = url;

    if (activeOrganizationNumber != null) {
      const queryKey = getAvtalerSummaryForLeverandørQueryKey({
        path: {
          organisasjonsNummer: activeOrganizationNumber,
        },
        client: httpClient,
      });
      queryClient.invalidateQueries({
        queryKey: queryKey,
      });
    }
  };

  return (
    <Button className={cn("w-fit", className)} variant="secondary" asChild>
      <Link href={undefined} onClick={handleClick}>
        {t("pages.bestillinger.eksporter-knapp")}
        <FileCsvFillIcon />
      </Link>
    </Button>
  );
};

const SistEksportertOgOppdatert = ({
  periode,
  className,
}: {
  periode: AvtalePeriodeSummaryWithEksportMetadataDto;
  className?: string;
}) => {
  const { t } = useTranslation();

  const hasExportedLatest =
    periode.csvEksportMetadata?.sistEksportert == null
      ? false
      : new Date(periode.csvEksportMetadata?.sistEksportert) >=
        new Date(periode.summary.lastUpdatedAt);

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div className="flex flex-row flex-wrap gap-2 items-center bg-balder-background-tinted p-2 rounded-lg text-base">
        <span className="flex items-center">
          {t("pages.bestillinger.fil-sist-eksportert")}:{" "}
          {periode.csvEksportMetadata?.sistEksportert == null
            ? t("pages.bestillinger.fil-sist-eksportert-aldri")
            : formatDateString(
                periode.csvEksportMetadata?.sistEksportert,
                "short",
                true,
              )}
          {hasExportedLatest && (
            <Tooltip
              content={t("pages.bestillinger.ingen-endringer-siden-sist")}
            >
              <CheckmarkIcon fontSize={"1.5rem"} color="rgb(16, 49, 35)" />
            </Tooltip>
          )}
        </span>
      </div>
      <span className="text-base">
        {t("pages.bestillinger.fil-sist-oppdatert")}:{" "}
        {formatDateString(periode.summary.lastUpdatedAt, "short", true)}
      </span>
    </div>
  );
};

const NyeEndringerSidenSistCsvEksport = ({
  periode,
  className,
}: {
  periode: AvtalePeriodeSummaryWithEksportMetadataDto;
  className?: string;
}) => {
  const { t } = useTranslation();

  const nyeBestillinger =
    periode.csvEksportMetadata?.nyeBestillinger ??
    periode.summary.summaryData.aktiveAvtaler;
  const nyeAvbestillinger =
    periode.csvEksportMetadata?.nyeAvbestillinger ??
    periode.summary.summaryData.avbestillinger;

  return periode.summary.periodIsInactive ? (
    <span>{t("pages.bestillinger.avtaleperiode-utgått")}</span>
  ) : (
    <div className={cn("space-y-1", className)}>
      <div className="flex gap-2 items-center">
        <HjelpeTekst
          hjelpeTekst={t("pages.bestillinger.nye-endringer-hjelpetekst")}
          ariaLabel={t("pages.bestillinger.nye-endringer-aria-label")}
        >
          <Heading level={4}>{t("pages.bestillinger.nye-endringer")}</Heading>
        </HjelpeTekst>
      </div>
      <div className="flex gap-2">
        <dt className="font-semibold">
          {t("pages.bestillinger.bestillinger")}:
        </dt>
        <dd className="m-0 whitespace-pre-line break-all text-pretty">
          {nyeBestillinger}
        </dd>
      </div>
      <div className="flex gap-2">
        <dt className="font-semibold">
          {t("pages.bestillinger.avbestillinger")}:
        </dt>
        <dd className="m-0 whitespace-pre-line break-all text-pretty">
          {nyeAvbestillinger}
        </dd>
      </div>
    </div>
  );
};

export default BestillingListe;
